import { PortableText, type PortableTextBlock } from "@portabletext/react";
import { cn } from "@/lib/utils";
import { ArticleCodeBlock } from "./articleCodeBlock";

type ArticleContentProps = {
  value: unknown[] | null | undefined;
  className?: string;
};

type CodeBlockValue = { _type?: string; code?: string; language?: string | null };

const components = {
  types: {
    code: ({ value }: { value?: CodeBlockValue }) => (
      <ArticleCodeBlock
        code={typeof value?.code === "string" ? value.code : ""}
        language={value?.language ?? undefined}
      />
    ),
  },
  block: {
    h1: ({ children }: { children?: React.ReactNode }) => (
      <h1 className="mt-10 mb-4 font-serif text-3xl font-semibold tracking-tight text-foreground first:mt-0">
        {children}
      </h1>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
      <h2 className="mt-8 mb-3 font-serif text-2xl font-semibold tracking-tight text-foreground">
        {children}
      </h2>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
      <h3 className="mt-6 mb-2 font-serif text-xl font-semibold text-foreground">
        {children}
      </h3>
    ),
    normal: ({ children }: { children?: React.ReactNode }) => (
      <p className="mb-4 leading-relaxed text-foreground/90">{children}</p>
    ),
    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <blockquote className="my-6 border-l-2 border-foreground/20 pl-6 italic text-foreground/80">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }: { children?: React.ReactNode }) => (
      <ul className="my-4 ml-6 list-disc space-y-2 text-foreground/90">{children}</ul>
    ),
    number: ({ children }: { children?: React.ReactNode }) => (
      <ol className="my-4 ml-6 list-decimal space-y-2 text-foreground/90">{children}</ol>
    ),
  },
  marks: {
    strong: ({ children }: { children?: React.ReactNode }) => (
      <strong className="font-semibold">{children}</strong>
    ),
    em: ({ children }: { children?: React.ReactNode }) => (
      <em className="italic">{children}</em>
    ),
    link: ({
      children,
      value,
    }: {
      children?: React.ReactNode;
      value?: { href?: string };
    }) => {
      const href = value?.href ?? "#";
      const isExternal = href.startsWith("http");
      return (
        <a
          href={href}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className="text-foreground underline underline-offset-4 transition-opacity hover:opacity-80"
        >
          {children}
        </a>
      );
    },
  },
};

export function ArticleContent({ value, className }: ArticleContentProps) {
  if (!value || !Array.isArray(value) || value.length === 0) {
    return null;
  }

  return (
    <div className={cn("article-content", className)}>
      <PortableText value={value as PortableTextBlock[]} components={components} />
    </div>
  );
}
